import { useState } from 'react';
import { Loader2, Pencil, Plus, Signpost, Trash2, Users } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import axiosInstance from '@/lib/axios';
import { useAvenueAssignments, useCommunityAvenues } from '@/hooks/use-community-avenues';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { AvenueMembersDialog } from './AvenueMembersDialog';
import type { CommunityAvenue } from '@/types';

interface AvenueSettingsProps {
  communityId: string;
}

export function AvenueSettings({ communityId }: AvenueSettingsProps) {
  const { avenues, isLoading, mutate } = useCommunityAvenues(communityId);
  const {
    membersByAvenue,
    isLoading: isLoadingAssignments,
    mutate: mutateAssignments,
  } = useAvenueAssignments(communityId);
  const [newName, setNewName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [editingAvenue, setEditingAvenue] = useState<CommunityAvenue | null>(null);
  const [editName, setEditName] = useState('');
  const [editActive, setEditActive] = useState(true);
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [deletingAvenue, setDeletingAvenue] = useState<CommunityAvenue | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [managingAvenue, setManagingAvenue] = useState<CommunityAvenue | null>(null);

  const refreshAssignments = () => {
    void mutateAssignments();
  };

  const handleCreate = async () => {
    const trimmed = newName.trim();
    if (!trimmed) {
      toast.error('Enter an avenue name');
      return;
    }

    setIsCreating(true);
    try {
      await axiosInstance.post(`/api/communities/${communityId}/avenues`, { name: trimmed });
      toast.success('Avenue added');
      setNewName('');
      mutate();
    } catch (error) {
      const message =
        axios.isAxiosError(error) &&
        error.response?.data &&
        typeof error.response.data === 'object' &&
        'error' in error.response.data
          ? String((error.response.data as { error: string }).error)
          : 'Failed to add avenue';
      toast.error(message);
    } finally {
      setIsCreating(false);
    }
  };

  const openEdit = (avenue: CommunityAvenue) => {
    setEditingAvenue(avenue);
    setEditName(avenue.name);
    setEditActive(avenue.isActive);
  };

  const handleSaveEdit = async () => {
    if (!editingAvenue) return;
    const trimmed = editName.trim();
    if (!trimmed) {
      toast.error('Enter an avenue name');
      return;
    }

    setIsSavingEdit(true);
    try {
      await axiosInstance.patch(`/api/communities/${communityId}/avenues/${editingAvenue.id}`, {
        name: trimmed,
        isActive: editActive,
      });
      toast.success('Avenue updated');
      setEditingAvenue(null);
      mutate();
    } catch (error) {
      const message =
        axios.isAxiosError(error) &&
        error.response?.data &&
        typeof error.response.data === 'object' &&
        'error' in error.response.data
          ? String((error.response.data as { error: string }).error)
          : 'Failed to update avenue';
      toast.error(message);
    } finally {
      setIsSavingEdit(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingAvenue) return;
    setIsDeleting(true);
    try {
      await axiosInstance.delete(`/api/communities/${communityId}/avenues/${deletingAvenue.id}`);
      toast.success('Avenue deleted');
      setDeletingAvenue(null);
      mutate();
      refreshAssignments();
    } catch (error) {
      const message =
        axios.isAxiosError(error) &&
        error.response?.data &&
        typeof error.response.data === 'object' &&
        'error' in error.response.data
          ? String((error.response.data as { error: string }).error)
          : 'Failed to delete avenue';
      toast.error(message);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Signpost className="h-5 w-5 text-primary" />
            District Avenues
          </CardTitle>
          <CardDescription>
            Define avenues for district projects and see which members belong to each one.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1 space-y-2">
              <Label htmlFor="new-avenue-name">New avenue name</Label>
              <Input
                id="new-avenue-name"
                value={newName}
                onChange={(event) => setNewName(event.target.value)}
                placeholder="e.g., Community Service"
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    void handleCreate();
                  }
                }}
              />
            </div>
            <div className="flex items-end">
              <Button onClick={() => void handleCreate()} disabled={isCreating}>
                {isCreating ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Plus className="mr-2 h-4 w-4" />
                )}
                Add Avenue
              </Button>
            </div>
          </div>

          {isLoading || isLoadingAssignments ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          ) : avenues.length === 0 ? (
            <p className="text-sm text-muted-foreground">No avenues defined yet.</p>
          ) : (
            <div className="divide-y rounded-xl border">
              {avenues.map((avenue) => {
                const members = membersByAvenue[avenue.id] ?? [];

                return (
                  <div key={avenue.id} className="px-4 py-4 space-y-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="font-medium truncate">{avenue.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {avenue.isActive ? 'Active' : 'Inactive'} · {members.length}{' '}
                          {members.length === 1 ? 'member' : 'members'}
                        </p>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <Button
                          variant="outline"
                          size="sm"
                          className="gap-2"
                          onClick={() => setManagingAvenue(avenue)}
                        >
                          <Users className="h-4 w-4" />
                          Members
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => openEdit(avenue)}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-destructive hover:text-destructive"
                          onClick={() => setDeletingAvenue(avenue)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>

                    {members.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {members.map((member) => (
                          <div
                            key={member.id}
                            className="inline-flex items-center gap-2 rounded-full border bg-muted/40 px-3 py-1.5"
                          >
                            <Avatar className="h-6 w-6">
                              <AvatarImage src={member.image} />
                              <AvatarFallback className="text-[10px]">
                                {member.name.substring(0, 2).toUpperCase()}
                              </AvatarFallback>
                            </Avatar>
                            <span className="text-sm">{member.name}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">No members assigned yet.</p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <AvenueMembersDialog
        communityId={communityId}
        avenue={managingAvenue}
        assignedMembers={managingAvenue ? (membersByAvenue[managingAvenue.id] ?? []) : []}
        onClose={() => setManagingAvenue(null)}
        onSaved={refreshAssignments}
      />

      <Dialog open={!!editingAvenue} onOpenChange={(open) => !open && setEditingAvenue(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Avenue</DialogTitle>
            <DialogDescription>Update the avenue name or visibility.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="edit-avenue-name">Name</Label>
              <Input
                id="edit-avenue-name"
                value={editName}
                onChange={(event) => setEditName(event.target.value)}
              />
            </div>
            <div className="flex items-center justify-between rounded-lg border px-4 py-3">
              <div>
                <Label htmlFor="edit-avenue-active">Active</Label>
                <p className="text-xs text-muted-foreground">
                  Inactive avenues cannot be selected for new projects.
                </p>
              </div>
              <Switch
                id="edit-avenue-active"
                checked={editActive}
                onCheckedChange={setEditActive}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingAvenue(null)}>
              Cancel
            </Button>
            <Button onClick={() => void handleSaveEdit()} disabled={isSavingEdit}>
              {isSavingEdit && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={!!deletingAvenue}
        onOpenChange={(open) => !open && setDeletingAvenue(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete avenue?</AlertDialogTitle>
            <AlertDialogDescription>
              {deletingAvenue
                ? `"${deletingAvenue.name}" will be removed. This is only allowed if no projects are linked to it.`
                : ''}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(event) => {
                event.preventDefault();
                void handleDelete();
              }}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
